import { Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import {
  videoType,
  vimeoCoverImageUrl,
  vimeoEmbedUrl,
  youtubeCoverImageUrl,
  youtubeEmbedUrl,
} from '@core/helpers/video';
import {
  AccountService,
  AuthService,
  ContentService,
  ContextService,
  FeaturesService,
  FilesService,
  MetaService,
  PlatformService,
  RequestService,
} from '@core/services';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  Audio as AudioContent,
  ContentTypeEnum,
  IImageFile,
  Image as ImageContent,
  ImageSizeEnum,
  Video as VideoContent,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { audioPlayerDefaultConfig, OnUiAudioPlayerConfig } from 'onclass-ui';
import { BaseContentCard, IContentCardState } from '../base-content-card';

interface IMediaCardComponentState extends IContentCardState {
  isMediaPrepared: boolean;
  showFullImage: boolean;
  isBigImage: boolean;
}

@Component({
  selector: 'app-media-card',
  templateUrl: './media-card.component.html',
  styleUrls: ['./media-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class MediaCardComponent extends BaseContentCard {
  @Input() show = true;
  @Input() canClose = false;
  @Input() inline = false;
  @Input() active = false;
  // tslint:disable-next-line: no-output-native
  @Output() close = new EventEmitter<boolean>();

  public me$ = this.contextService.me$;
  public fullTitle: string;
  public accountRoute = AccountService.accountRoute;
  public state: IMediaCardComponentState = {
    ...super.getState(),
    isMediaPrepared: false,
    showFullImage: false,
    isBigImage: false,
  };
  public schemaOrgItemtype: string;
  public imageUrl: string;
  public safeMediaUrl: SafeResourceUrl;
  public audioPlayerConfig: OnUiAudioPlayerConfig = {
    ...audioPlayerDefaultConfig,
  };
  protected _image: IImageFile;

  constructor(
    protected accountService: AccountService,
    protected contextService: ContextService,
    protected contentService: ContentService,
    protected authService: AuthService,
    protected features: FeaturesService,
    protected router: Router,
    protected metaService: MetaService,
    protected translate: TranslateService,
    protected location: Location,
    protected readonly request: RequestService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
    public deviceService: DeviceDetectorService,
    protected sanitizer: DomSanitizer,
    protected store: Store,
  ) {
    super(
      accountService,
      contextService,
      contentService,
      authService,
      features,
      router,
      metaService,
      translate,
      location,
      request,
      cdr,
      platform,
      store,
    );
  }

  protected init() {
    super.init();
    this.state.showFullImage = false;
    this.state.isBigImage = false;

    this.prepareMedia();

    if (this.isVideo) {
      this.schemaOrgItemtype = 'https://schema.org/MediaObject';
    } else if (this.isAudio) {
      this.schemaOrgItemtype = 'https://schema.org/PodcastEpisode';
    } else if (this.isImage) {
      this.schemaOrgItemtype = 'https://schema.org/MediaObject';
    }

    this.setInterval(() => {
      // this.audioPlayerConfig.displayTitle = !this.audioPlayerConfig.displayTitle;
      // this.audioPlayer.detect
      this.cdr.detectChanges();
    }, 500);
  }

  public get isCutable(): boolean {
    const desc = this._item.description || '';
    return desc.length > this.cutMaxLength;
  }

  public get isVideo(): boolean {
    return this._item.type === ContentTypeEnum.video;
  }

  public get isAudio(): boolean {
    return this._item.type === ContentTypeEnum.audio;
  }

  public get isImage(): boolean {
    return this._item.type === ContentTypeEnum.image;
  }

  public prepareMedia() {
    if (this.isVideo) {
      const _video = this._item as VideoContent;
      const embedUrl = youtubeEmbedUrl(_video.media_url) || vimeoEmbedUrl(_video.media_url);
      if (embedUrl) {
        this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
        this.state.isMediaPrepared = true;
      } else {
        this.safeMediaUrl = null;
        this.state.isMediaPrepared = false;
      }
    } else if (this.isAudio) {
      const _audio = this._item as AudioContent;
      const mp3File = FilesService.getFileUrlBySize(_audio.media_id);
      if (mp3File) {
        this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(mp3File);
        this.audioPlayerConfig.singleTrack[0].link = mp3File;
        this.state.isMediaPrepared = true;
      }
    } else if (this.isImage) {
      const _image = this._item as ImageContent;
      this._image = (_image?.media?.images as any).find((el) => el.size == ImageSizeEnum.wide);
      this.state.isBigImage = this._image && parseInt(String(this._image.height), 10) > 400;
      this.imageUrl = FilesService.getFileUrlBySize(_image.media_id, ImageSizeEnum.wide);
    }
    this.detectChanges();
  }

  protected prepareMetaImage() {
    if (!this._item.cover && this.isVideo) {
      const _video = this._item as VideoContent;
      const _type = videoType(_video.media_url);
      switch (_type) {
        case 'youtube':
          this.coverImageUrl = this.metaImageUrl = youtubeCoverImageUrl(_video.media_url, 'maxresdefault');
          break;

        case 'vimeo':
          this.coverImageUrl = this.metaImageUrl = vimeoCoverImageUrl(_video.media_url);
          break;
      }
      return;
    } else {
      super.prepareMetaImage();
    }
  }

  public showFullImage() {
    if (!this.deviceService.isDesktop()) {
      return;
    }

    if (this.state.isBigImage) {
      this.state.showFullImage = true;
      this.detectChanges();

      this.setupMeta();
    }
  }

  public hideFullImage() {
    this.state.showFullImage = false;
    this.resetMeta();
    this.detectChanges();
  }
}
